/**
 * Service worker updates (staging: the old build kept showing after a reload).
 *
 * The worker precaches index.html and every asset, so a reload right after a
 * deploy is served from the old precache while the new worker installs. The
 * new worker skips waiting and claims open pages (vite-plugin-pwa autoUpdate:
 * skipWaiting + clientsClaim); this module reacts to that takeover:
 *  - safe (nothing typed and unsaved, no field focused): reload into the new
 *    build right away;
 *  - not safe: show "New version — tap to update" and reload by itself as soon
 *    as it is safe (after a few quiet seconds, or when the app goes to the
 *    background).
 * Unsynced *data* is never at risk: it lives in IndexedDB and the outbox, which
 * survive a reload. It also checks for a new build when the app comes back to
 * the foreground and every 30 minutes.
 */
import { create } from "zustand";
import { hasUnsaved, useUnsavedStore } from "./unsaved";

export const useAppUpdateStore = create<{ ready: boolean }>(() => ({ ready: false }));

const QUIET_MS = 3000;
const CHECK_EVERY_MS = 30 * 60 * 1000;

function editableFocused(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
}

export function isSafeToReload(): boolean {
  return !hasUnsaved() && !editableFocused();
}

let reloading = false;
function reloadNow(): void {
  if (reloading) return;
  reloading = true;
  window.location.reload();
}

/** Called when a new build has taken over this page. */
export function onNewVersion(): void {
  if (reloading) return;
  if (isSafeToReload()) {
    reloadNow();
    return;
  }
  useAppUpdateStore.setState({ ready: true });
  let timer: ReturnType<typeof setTimeout> | null = null;
  const check = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      if (isSafeToReload()) reloadNow();
    }, QUIET_MS);
  };
  document.addEventListener("focusout", check);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && isSafeToReload()) reloadNow();
  });
  useUnsavedStore.subscribe(check);
}

/** User tapped "Update": reload now (they chose to). */
export function applyUpdateNow(): void {
  reloadNow();
}

export function registerServiceWorker(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  if (import.meta.env.DEV) return;
  const sw = navigator.serviceWorker;
  // First install claims the page too; that isn't a new version.
  let hadController = sw.controller != null;
  sw.addEventListener("controllerchange", () => {
    if (!hadController) {
      hadController = true;
      return;
    }
    onNewVersion();
  });
  const register = () => {
    sw.register("/sw.js", { scope: "/" })
      .then((reg) => {
        const check = () => void reg.update().catch(() => undefined);
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") check();
        });
        setInterval(check, CHECK_EVERY_MS);
      })
      .catch(() => undefined);
  };
  if (document.readyState === "complete") register();
  else window.addEventListener("load", register, { once: true });
}
